import { CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { Download } from "@mui/icons-material";

import { ReportsInfoTooltip } from "@/components";
import DeleteIcon from "@/assets/svgs/garbage.svg";
import { formatDateOrTime, numberWithCommas } from "@/utils";
import { parseSelectedOptions, getAdjustmentTypeLabel } from "@/utils/report-utils";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tokens } from "@/tokens";
const StyledContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});
import { useReportDownload } from "@/hooks";

interface IColumnActions {
  action: (type: "download" | "delete", report: IReport) => void;
  actionsLoading: { delete: string[]; download: string[] };
}

export const reportColumns = ({
  action,
  actionsLoading,
}: IColumnActions): IColumn<IReport>[] => {
  const { downloadReport, downloadingReports } = useReportDownload();

  return [
    {
      name: "Date & Time",
      key: "createdAt",
      align: "left",
      width: "22%",
      minWidth: "160px",
      component: ({ createdAt }) => (
        <>
          <Typography variant="body2" color={tokens.text.primary} sx={{ fontSize: { xs: '11px', md: '12px' } }}>
            {formatDateOrTime(createdAt ?? new Date(), "date")}
          </Typography>
          <Typography variant="body1" fontSize={tokens.fontSize.base} color={tokens.text.muted}>
            {formatDateOrTime(createdAt ?? new Date(), "time")}
          </Typography>
        </>
      ),
    },
    {
      name: "# of Entries",
      key: "entryCount",
      align: "center",
      width: "12%",
      minWidth: "90px",
      component: ({ entryCount }) => (
        <StyledContainer sx={{ '& *': { fontSize: { xs: '11px', md: '12px' } } }}>{numberWithCommas(entryCount || 0)}</StyledContainer>
      ),
    },
    {
      name: "Adjustment Type",
      key: "selectedOptions",
      align: "center",
      width: "22%",
      minWidth: "160px",
      component: ({ selectedOptions }) => {
        if (!selectedOptions) {
          return <StyledContainer>-</StyledContainer>;
        }

        try {
          const parsed = parseSelectedOptions(selectedOptions);
          const typeLabel = getAdjustmentTypeLabel(parsed.adjustmentType);

          return (
            <StyledContainer sx={{ flexDirection: 'column', gap: '2px', '& *': { fontSize: { xs: '11px', md: '12px' } } }}>
              <Tooltip title={typeLabel}>
                <Typography variant="body2" color={tokens.text.primary} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {typeLabel}
                </Typography>
              </Tooltip>
              {parsed.adjustmentValue && (
                <Tooltip title={parsed.adjustmentValue}>
                  <Typography variant="caption" color={tokens.text.muted} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {parsed.adjustmentValue}
                  </Typography>
                </Tooltip>
              )}
            </StyledContainer>
          );
        } catch (error) {
          return <StyledContainer>-</StyledContainer>;
        }
      },
    },
    {
      name: "Information",
      key: "_id",
      align: "center",
      width: "10%",
      minWidth: "80px",
      component: (report) => (
        <StyledContainer>
          <ReportsInfoTooltip report={report} />
        </StyledContainer>
      ),
    },
    {
      name: "Actions",
      key: "actions",
      align: "center",
      width: "10%",
      minWidth: "80px",
      component: (report) => {
        const { _id, isCompleted } = report;

        const isDownloading = downloadingReports.includes(_id);
        const isDeleting = actionsLoading["delete"].includes(_id);

        const handleDownload = async () => {
          try {
            await downloadReport(_id);
          } catch (error) {}
        };

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Tooltip title={!isCompleted ? "Processing..." : "Download Report"}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  disabled={isDownloading || !isCompleted}
                  sx={{ color: tokens.text.primary, p: 0.5 }}
                >
                  {isDownloading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Download sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => action("delete", report)}
                disabled={isDeleting}
                sx={{
                  color: tokens.text.primary,
                  p: 0.5,
                  "&:hover": { color: tokens.status.danger },
                }}
              >
                {isDeleting ? (
                  <CircularProgress size={14} />
                ) : (
                  <img
                    src={DeleteIcon}
                    alt="Delete"
                    style={{ width: 14, height: 14 }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];
};
